import logger from '@utils/logger'
import {
  DISABLED_RELEASE_CAMPAIGN,
  normalizeReleaseCampaign,
  RELEASE_CAMPAIGN_CACHE_TTL_MS,
} from '@utils/releaseCampaign'
import {
  EVALUATED_FEATURES_CACHE_TTL_MS,
  isEvaluatedFeatureEnabled,
} from '@utils/featureFlags'
/**
 * Configuration Service
 * Fetches and caches centralized configuration from the server
 */

import { api } from './enhancedApiService'

export { RELEASE_CAMPAIGN_CACHE_TTL_MS } from '@utils/releaseCampaign'
export { EVALUATED_FEATURES_CACHE_TTL_MS } from '@utils/featureFlags'

const UNKNOWN_EVALUATED_FEATURES = Object.freeze({
  env: 'unknown',
  features: Object.freeze({}),
})

class ConfigService {
  constructor() {
    this.cache = {
      paperTrading: null,
      client: null,
      dashboard: null,
      releaseCampaign: null,
    }
    this.loading = {}
  }

  /**
   * Get paper trading configuration
   * @returns {Promise<Object>} Paper trading config
   */
  async getPaperTradingConfig() {
    if (this.cache.paperTrading) {
      return this.cache.paperTrading
    }

    if (this.loading.paperTrading) {
      return this.loading.paperTrading
    }

    this.loading.paperTrading = api
      .get('/config/paper-trading')
      .then((response) => {
        this.cache.paperTrading = response.data.data
        delete this.loading.paperTrading
        return this.cache.paperTrading
      })
      .catch((error) => {
        delete this.loading.paperTrading
        logger.error(
          '[ConfigService] Failed to fetch paper trading config:',
          error,
        )

        // Return default fallback config
        return this.getDefaultPaperTradingConfig()
      })

    return this.loading.paperTrading
  }

  /**
   * Get evaluated feature flags for current environment
   * Returns boolean values for each feature indicating availability
   * @param {string} [namespace] - Optional namespace to filter (e.g., 'dashboard')
   * @param {{ force?: boolean }} [options] - Bypass a valid cache entry
   * @returns {Promise<Object>} Evaluated features with env info
   */
  async getEvaluatedFeatures(namespace, { force = false } = {}) {
    const cacheKey = `evaluatedFeatures_${namespace || 'all'}`
    const cached = this.cache[cacheKey]
    const cacheIsFresh =
      cached &&
      Date.now() - cached.fetchedAt < EVALUATED_FEATURES_CACHE_TTL_MS

    if (!force && cacheIsFresh) {
      return cached.data
    }

    if (this.loading[cacheKey]) {
      return this.loading[cacheKey]
    }

    const url = namespace
      ? `/config/features/evaluated?namespace=${encodeURIComponent(namespace)}`
      : '/config/features/evaluated'

    this.loading[cacheKey] = Promise.resolve()
      .then(() => api.get(url))
      .then((response) => {
        const evaluated = response.data.data
        this.cache[cacheKey] = {
          data: evaluated,
          fetchedAt: Date.now(),
        }
        delete this.loading[cacheKey]
        return evaluated
      })
      .catch((error) => {
        delete this.loading[cacheKey]
        logger.error(
          '[ConfigService] Failed to fetch evaluated features:',
          error,
        )

        const fallback = cached?.data ?? UNKNOWN_EVALUATED_FEATURES
        this.cache[cacheKey] = {
          data: fallback,
          fetchedAt: Date.now(),
        }
        return fallback
      })

    return this.loading[cacheKey]
  }

  /**
   * Check if a specific feature is enabled
   * @param {string} featurePath - Feature path (e.g., 'dashboard.apiPlayground')
   * @returns {Promise<boolean>} Whether the feature is enabled
   */
  async isFeatureEnabled(featurePath) {
    try {
      const evaluated = await this.getEvaluatedFeatures()
      return isEvaluatedFeatureEnabled(evaluated, featurePath)
    } catch (error) {
      logger.error('[ConfigService] Failed to check feature:', error)
      return false
    }
  }

  /**
   * Get client configuration
   * @returns {Promise<Object>} Client config
   */
  async getClientConfig() {
    if (this.cache.client) {
      return this.cache.client
    }

    if (this.loading.client) {
      return this.loading.client
    }

    this.loading.client = api
      .get('/config/client')
      .then((response) => {
        this.cache.client = response.data.data
        delete this.loading.client
        return this.cache.client
      })
      .catch((error) => {
        delete this.loading.client
        logger.error('[ConfigService] Failed to fetch client config:', error)
        return {}
      })

    return this.loading.client
  }

  /**
   * Get dashboard configuration
   * @returns {Promise<Object>} Dashboard config
   */
  async getDashboardConfig() {
    if (this.cache.dashboard) {
      return this.cache.dashboard
    }

    if (this.loading.dashboard) {
      return this.loading.dashboard
    }

    this.loading.dashboard = api
      .get('/config/dashboard')
      .then((response) => {
        this.cache.dashboard = response.data.data
        delete this.loading.dashboard
        return this.cache.dashboard
      })
      .catch((error) => {
        delete this.loading.dashboard
        logger.error('[ConfigService] Failed to fetch dashboard config:', error)
        return {}
      })

    return this.loading.dashboard
  }

  /**
   * Get the current product release campaign.
   * A bounded TTL avoids requesting configuration on every render.
   * @param {{ force?: boolean }} [options] - Bypass a valid cache entry
   * @returns {Promise<Object>} Release campaign or the disabled fallback
   */
  async getReleaseCampaign({ force = false } = {}) {
    const cached = this.cache.releaseCampaign
    const cacheIsFresh =
      cached && Date.now() - cached.fetchedAt < RELEASE_CAMPAIGN_CACHE_TTL_MS

    if (!force && cacheIsFresh) {
      return cached.data
    }

    if (this.loading.releaseCampaign) {
      return this.loading.releaseCampaign
    }

    this.loading.releaseCampaign = Promise.resolve()
      .then(() => api.get('/config/release-campaign'))
      .then((response) => {
        const campaign = normalizeReleaseCampaign(response.data?.data)

        this.cache.releaseCampaign = {
          data: campaign,
          fetchedAt: Date.now(),
        }
        delete this.loading.releaseCampaign
        return campaign
      })
      .catch((error) => {
        delete this.loading.releaseCampaign
        logger.error('[ConfigService] Failed to fetch release campaign:', error)

        const fallback = cached?.data ?? DISABLED_RELEASE_CAMPAIGN
        this.cache.releaseCampaign = {
          data: fallback,
          fetchedAt: Date.now(),
        }
        return fallback
      })

    return this.loading.releaseCampaign
  }

  /**
   * Clear cache (useful for refreshing configuration)
   */
  clearCache() {
    this.cache = {
      paperTrading: null,
      client: null,
      dashboard: null,
      releaseCampaign: null,
    }
  }

  /**
   * Default fallback configuration for paper trading
   * Used when API is unavailable
   */
  getDefaultPaperTradingConfig() {
    return {
      enabled: true,
      account: {
        defaultCapital: 100000,
        maxAccounts: 10,
      },
      websocket: {
        enabled: true,
        path: '/ws',
        reconnect: {
          baseDelay: 1000,
          maxDelay: 30000,
          maxAttempts: 10,
          backoffMultiplier: 1.5,
          jitter: true,
        },
        feeds: {
          maxOrderHistory: 100,
          maxTradeHistory: 100,
        },
      },
      analytics: {
        enabled: true,
        charts: {
          equityCurve: { tension: 0.4, borderWidth: 2, fill: true },
          drawdown: { tension: 0.4, borderWidth: 2, max: 0, fill: true },
          histogram: { bins: 20, borderWidth: 1 },
          scatter: { pointRadius: 5, pointHoverRadius: 7 },
          pie: { cutout: '50%', borderWidth: 2 },
          line: { tension: 0.4, borderWidth: 2 },
        },
        colors: {
          primary: 'rgb(75, 192, 192)',
          success: 'rgb(75, 192, 192)',
          error: 'rgb(255, 99, 132)',
          warning: 'rgb(255, 206, 86)',
          info: 'rgb(54, 162, 235)',
          neutral: 'rgb(201, 203, 207)',
        },
        metrics: {
          sharpeRatio: { excellent: 2.0, good: 1.0, fair: 0.0, poor: -1.0 },
          sortinoRatio: { excellent: 2.0, good: 1.0, fair: 0.0, poor: -1.0 },
          profitFactor: { excellent: 2.0, good: 1.5, fair: 1.0, poor: 0.5 },
          winRate: { excellent: 60, good: 50, fair: 40, poor: 30 },
          maxDrawdown: { excellent: 5, good: 10, fair: 20, poor: 30 },
        },
        heatmap: {
          darkGreen: '#1b5e20',
          green: '#388e3c',
          lightGreen: '#81c784',
          neutral: '#e0e0e0',
          lightRed: '#ef9a9a',
          red: '#e57373',
          darkRed: '#c62828',
          thresholds: {
            excellent: 5,
            good: 2,
            fair: 0,
            poor: -2,
            bad: -5,
          },
        },
        defaults: {
          timeframe: '90d',
          maxStrategiesCompare: 6,
          annualizationFactor: 252,
          chartHeight: {
            small: 250,
            medium: 300,
            large: 350,
            xlarge: 400,
          },
        },
      },
    }
  }

  /**
   * Helper: Get WebSocket URL from config
   */
  async getWebSocketUrl() {
    const paperTradingConfig = await this.getPaperTradingConfig()
    const wsPath = paperTradingConfig.websocket.path

    // Construct WebSocket URL based on current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = import.meta.env.VITE_WS_HOST || window.location.hostname
    const port = import.meta.env.VITE_WS_PORT

    // In production (HTTPS), use the same port as the website (443) via Nginx proxy
    // In development, use explicit port 8001
    if (port) {
      return `${protocol}//${host}:${port}${wsPath}`
    } else if (protocol === 'wss:') {
      // Production: WebSocket is proxied through Nginx on standard HTTPS port
      return `${protocol}//${host}${wsPath}`
    } else {
      // Development fallback: use port 8001
      return `${protocol}//${host}:8001${wsPath}`
    }
  }
}

// Export singleton instance
const configService = new ConfigService()
export default configService
